import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    templateMarketplaceApi,
    TemplateDetail,
    TemplateSummary,
    InstallResult,
    AlreadyInstalled,
} from "../../services/apis/templateMarketplace.api";
import { RootState } from "../store";

export type QuickAccess = "all" | "starred" | "recent" | "mine";

export interface MarketplaceFilters {
    sector?: string;
    tag?: string;
    scope: "global" | "org" | "all";
    search: string;
    quickAccess: QuickAccess;
}

export type InstallStatus =
    | { status: "idle" }
    | { status: "pending" }
    | { status: "success"; result: InstallResult }
    | { status: "alreadyInstalled"; existing: AlreadyInstalled }
    | { status: "error"; error: string };

interface State {
    summaries: TemplateSummary[];
    detailById: Record<string, TemplateDetail>;
    filters: MarketplaceFilters;
    selectedId: string | null;
    loadingList: boolean;
    loadingDetail: boolean;
    install: InstallStatus;
    error: string | null;
}

const initialState: State = {
    summaries: [],
    detailById: {},
    filters: {
        scope: "all",
        search: "",
        quickAccess: "all",
    },
    selectedId: null,
    loadingList: false,
    loadingDetail: false,
    install: { status: "idle" },
    error: null,
};

function orgIdFromState(state: any): string | undefined {
    const org = state?.organization?.selectedOrganization;
    if (!org) return undefined;
    return typeof org === "string" ? org : org?._id;
}

export const fetchTemplates = createAsyncThunk<
    TemplateSummary[],
    Partial<MarketplaceFilters> | undefined,
    { state: RootState }
>("templateMarketplace/fetchTemplates", async (override, { getState }) => {
    const state = getState();
    const filters = { ...state.templateMarketplace.filters, ...(override || {}) };
    const organizationId = orgIdFromState(state);
    return await templateMarketplaceApi.list({
        sector: filters.sector,
        tag: filters.tag,
        scope: filters.scope,
        search: filters.search,
        quickAccess: filters.quickAccess,
        organizationId,
    });
});

export const fetchTemplateById = createAsyncThunk<
    TemplateDetail,
    string,
    { state: RootState }
>("templateMarketplace/fetchTemplateById", async (id, { getState }) => {
    const organizationId = orgIdFromState(getState());
    return await templateMarketplaceApi.getById(id, organizationId);
});

export const toggleStarThunk = createAsyncThunk<
    { id: string; starred: boolean },
    string,
    { state: RootState }
>("templateMarketplace/toggleStar", async (id, { getState }) => {
    const organizationId = orgIdFromState(getState());
    const res = await templateMarketplaceApi.toggleStar(id, organizationId);
    return { id, starred: res.starred };
});

export const duplicateTemplateThunk = createAsyncThunk<
    TemplateDetail,
    { id: string; displayName: string },
    { state: RootState }
>("templateMarketplace/duplicate", async ({ id, displayName }, { getState }) => {
    const organizationId = orgIdFromState(getState());
    return await templateMarketplaceApi.duplicate(id, displayName, organizationId);
});

export const installTemplateThunk = createAsyncThunk<
    { kind: "installed"; data: InstallResult } | { kind: "alreadyInstalled"; data: AlreadyInstalled },
    { id: string; name?: string; title?: string; isPACT?: boolean },
    { state: RootState; rejectValue: string }
>(
    "templateMarketplace/install",
    async ({ id, name, title, isPACT }, { getState, rejectWithValue }) => {
        const organizationId = orgIdFromState(getState());
        try {
            return await templateMarketplaceApi.install(id, {
                organizationId,
                name,
                title,
                isPACT,
            });
        } catch (err: any) {
            return rejectWithValue(err?.message || "Install failed");
        }
    }
);

export const createCustomTemplateThunk = createAsyncThunk<
    TemplateDetail,
    Partial<TemplateDetail> & { displayName: string },
    { state: RootState }
>("templateMarketplace/create", async (body, { getState }) => {
    const organizationId = orgIdFromState(getState());
    return await templateMarketplaceApi.create({ ...body, organizationId });
});

export const updateCustomTemplateThunk = createAsyncThunk<
    TemplateDetail,
    { id: string; body: Partial<TemplateDetail> },
    { state: RootState }
>("templateMarketplace/update", async ({ id, body }, { getState }) => {
    const organizationId = orgIdFromState(getState());
    return await templateMarketplaceApi.update(id, { ...body, organizationId });
});

export const deleteCustomTemplateThunk = createAsyncThunk<
    string,
    string,
    { state: RootState }
>("templateMarketplace/delete", async (id, { getState }) => {
    const organizationId = orgIdFromState(getState());
    await templateMarketplaceApi.remove(id, organizationId);
    return id;
});

const slice = createSlice({
    name: "templateMarketplace",
    initialState,
    reducers: {
        setFilter(state, action: PayloadAction<Partial<MarketplaceFilters>>) {
            state.filters = { ...state.filters, ...action.payload };
        },
        setSelectedId(state, action: PayloadAction<string | null>) {
            state.selectedId = action.payload;
        },
        resetInstallStatus(state) {
            state.install = { status: "idle" };
        },
    },
    extraReducers: (b) => {
        b.addCase(fetchTemplates.pending, (s) => {
            s.loadingList = true;
            s.error = null;
        });
        b.addCase(fetchTemplates.fulfilled, (s, a) => {
            s.loadingList = false;
            s.summaries = a.payload;
        });
        b.addCase(fetchTemplates.rejected, (s, a) => {
            s.loadingList = false;
            s.error = a.error.message || "Failed to load templates";
        });

        b.addCase(fetchTemplateById.pending, (s) => {
            s.loadingDetail = true;
        });
        b.addCase(fetchTemplateById.fulfilled, (s, a) => {
            s.loadingDetail = false;
            s.detailById[a.payload._id] = a.payload;
            s.selectedId = a.payload._id;
            // merge enriched flags back into the summary row
            const idx = s.summaries.findIndex((x) => x._id === a.payload._id);
            if (idx >= 0) {
                s.summaries[idx] = {
                    ...s.summaries[idx],
                    isStarred: a.payload.isStarred,
                    isInstalled: a.payload.isInstalled,
                    installedAuditCategoryId: a.payload.installedAuditCategoryId,
                    lastViewedAt: a.payload.lastViewedAt,
                };
            }
        });
        b.addCase(fetchTemplateById.rejected, (s, a) => {
            s.loadingDetail = false;
            s.error = a.error.message || "Failed to load template";
        });

        b.addCase(toggleStarThunk.fulfilled, (s, a) => {
            const { id, starred } = a.payload;
            const sum = s.summaries.find((x) => x._id === id);
            if (sum) sum.isStarred = starred;
            const det = s.detailById[id];
            if (det) det.isStarred = starred;
        });

        b.addCase(duplicateTemplateThunk.fulfilled, (s, a) => {
            s.summaries.unshift({
                _id: a.payload._id,
                templateId: a.payload.templateId,
                scope: a.payload.scope,
                organizationId: a.payload.organizationId,
                parentTemplateId: a.payload.parentTemplateId,
                sector: a.payload.sector,
                tag: a.payload.tag,
                displayName: a.payload.displayName,
                summary: a.payload.summary,
                method: a.payload.method,
                framework: a.payload.framework,
                audience: a.payload.audience,
                expectedOutput: a.payload.expectedOutput,
                turnaround: a.payload.turnaround,
                stats: a.payload.stats,
                presetQuestionPapers: (a.payload.presetQuestionPapersList || []).length,
                isStarred: false,
                isInstalled: false,
                installedAuditCategoryId: null,
                lastViewedAt: null,
                // Tier 1 signals - duplicates start fresh
                installCount: 0,
                isVerified: false,
                ratingCount: 0,
                updatedAt: a.payload.updatedAt,
            });
            s.detailById[a.payload._id] = a.payload;
        });

        b.addCase(installTemplateThunk.pending, (s) => {
            s.install = { status: "pending" };
        });
        b.addCase(installTemplateThunk.fulfilled, (s, a) => {
            if (a.payload.kind === "installed") {
                s.install = { status: "success", result: a.payload.data };
                // mark as installed on summary + detail
                const sId = s.selectedId;
                if (sId) {
                    const sum = s.summaries.find((x) => x._id === sId);
                    if (sum) {
                        sum.isInstalled = true;
                        sum.installedAuditCategoryId = a.payload.data.auditCategoryId;
                    }
                    const det = s.detailById[sId];
                    if (det) {
                        det.isInstalled = true;
                        det.installedAuditCategoryId = a.payload.data.auditCategoryId;
                    }
                }
            } else {
                s.install = { status: "alreadyInstalled", existing: a.payload.data };
                const sId = s.selectedId;
                if (sId) {
                    const sum = s.summaries.find((x) => x._id === sId);
                    if (sum) {
                        sum.isInstalled = true;
                        sum.installedAuditCategoryId = a.payload.data.auditCategoryId;
                    }
                    const det = s.detailById[sId];
                    if (det) {
                        det.isInstalled = true;
                        det.installedAuditCategoryId = a.payload.data.auditCategoryId;
                    }
                }
            }
        });
        b.addCase(installTemplateThunk.rejected, (s, a) => {
            s.install = {
                status: "error",
                error: a.payload || a.error.message || "Install failed",
            };
        });

        b.addCase(updateCustomTemplateThunk.fulfilled, (s, a) => {
            s.detailById[a.payload._id] = a.payload;
        });

        b.addCase(deleteCustomTemplateThunk.fulfilled, (s, a) => {
            const id = a.payload;
            s.summaries = s.summaries.filter((x) => x._id !== id);
            delete s.detailById[id];
            if (s.selectedId === id) s.selectedId = null;
        });
    },
});

export const {
    setFilter,
    setSelectedId,
    resetInstallStatus,
} = slice.actions;
export const templateMarketplaceReducer = slice.reducer;
export default slice.reducer;
